/**
 * Decide whether a `fetch` call just wrote something a screen might be showing.
 *
 * Kept apart from the provider so it can be unit tested without a DOM: the
 * judgement calls below (which POSTs are really reads, which origins count) are
 * the part most likely to need revisiting as routes are added.
 */

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Same-origin paths that use a write method but do not write anything a user
 * is looking at. Matched as prefixes.
 *
 * `/api/ai/` is excluded for a second reason as well: those responses stream,
 * so `response.ok` resolves when the first byte arrives rather than when the
 * turn is finished, and refetching the whole app mid-stream would be both
 * early and pointless. The assistant is read-only by design.
 */
const NON_WRITING_API_PATHS = [
  '/api/ai/',
  '/api/extract-demo',
  '/api/health/',
  '/api/search',
  '/api/session/pulse',
  '/api/smart-upload/clarify',
  '/api/tools/',
  '/api/version',
];

/**
 * Supabase paths worth reacting to. Several pages write through `supabase-js`
 * in the browser rather than through a route handler, and a stock level added
 * that way has to show up like any other change.
 *
 * `/auth/v1/` is deliberately absent: token refreshes are POSTs that fire on a
 * timer, and treating them as writes would refetch the entire app every few
 * minutes for nothing.
 */
const SUPABASE_DATA_PATHS = ['/rest/v1/', '/storage/v1/object'];

interface RequestFacts {
  method: string;
  url: URL;
  isServerAction: boolean;
}

function readHeader(init: RequestInit | undefined, name: string) {
  const headers = init?.headers;

  if (!headers) return null;

  if (headers instanceof Headers) return headers.get(name);

  if (Array.isArray(headers)) {
    const match = headers.find(
      ([key]) => key?.toLowerCase() === name.toLowerCase(),
    );

    return match?.[1] ?? null;
  }

  const match = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === name.toLowerCase(),
  );

  return match?.[1] ?? null;
}

export function describeRequest(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  origin: string,
): RequestFacts | null {
  const isRequest = typeof Request !== 'undefined' && input instanceof Request;

  const method = (
    init?.method ?? (isRequest ? input.method : 'GET')
  ).toUpperCase();

  const rawUrl = isRequest ? input.url : String(input);

  let url: URL;

  try {
    url = new URL(rawUrl, origin);
  } catch {
    return null;
  }

  /*
   * Next posts server actions to the current page URL, so the path says
   * nothing. This header is how the router itself tells them apart.
   */
  const isServerAction =
    readHeader(init, 'next-action') !== null ||
    (isRequest && input.headers.get('next-action') !== null);

  return { method, url, isServerAction };
}

export function isMutatingRequest(
  facts: RequestFacts,
  origin: string,
  supabaseUrl: string | undefined,
) {
  if (!WRITE_METHODS.has(facts.method)) return false;

  const { url } = facts;

  if (url.origin === origin) {
    /*
     * A server action that already called `revalidatePath` gets refreshed
     * twice over, which costs one extra request and saves us from having to
     * know which actions remembered to.
     */
    if (facts.isServerAction) return true;

    if (!url.pathname.startsWith('/api/')) return false;

    return !NON_WRITING_API_PATHS.some((path) => url.pathname.startsWith(path));
  }

  if (supabaseUrl) {
    let supabaseOrigin: string | null = null;

    try {
      supabaseOrigin = new URL(supabaseUrl).origin;
    } catch {
      supabaseOrigin = null;
    }

    if (supabaseOrigin && url.origin === supabaseOrigin) {
      return SUPABASE_DATA_PATHS.some((path) => url.pathname.startsWith(path));
    }
  }

  return false;
}
